import React, { useMemo, useCallback } from 'react';
import { useRoleDelete, useRoleGetFilters, useRoleList } from '../hooks/use-role.hook';
import type { ColumnConfig } from '../../../../common/PaginatedTable';
import type { Role } from '../types/role.types';
import RoleTable from '../components/role-table.component';
import { DEFAULT_PAGINATED_TABLE_DATA } from '../../../../../const/default-paginated-table-data.const';
import ActionButtons from '../../../../common/ActionButtons';
import { useFilters } from '../../../../../hooks/use-filters.hook';
import { message } from 'antd';
import HeaderRow from '../../../../common/HeaderRow';
import { useTranslation } from 'react-i18next';
import { useAuthUser } from '../../../../../states/user.atom';
import { rbac } from 'permissions/permissions';
import { WithPermissionCheck } from '../../../../auth/WithPermissionCheck';
import { extractCreateError } from '@services/extract-api-error.service';

const RoleTableContainer = () => {
  const { t } = useTranslation();
  const { getUserPermissions } = useAuthUser();
  const userPermissions = getUserPermissions();

  const {
    queryParams,
    handlePageChange,
    handlePageSizeChange,
    handleFilterChange,
    handleSearchChange,
    handleSortChange,
  } = useFilters();

  const { data: availableFilters, isLoading: isAvailableFiltersLoading } = useRoleGetFilters();

  const { data, isLoading, isFetching } = useRoleList(queryParams);
  const { docs: tableData = [] } = data ?? DEFAULT_PAGINATED_TABLE_DATA;

  const deleteRoleMutation = useRoleDelete();

  const handleDeleteRole = useCallback(async (record: Role) => {
    try {
      await deleteRoleMutation.mutateAsync(record.id);
      message.success(t('crud.delete.success', { entity: t('roles.title') }));
    } catch (error) {
      const errorMessage = extractCreateError(
        error,
        t('crud.delete.error', { entity: t('roles.title') })
      );
      message.error(errorMessage);
    }
  }, []);

  // Handle table sorting
  const handleTableChange = useCallback(
    (pagination: any, filters: any, sorter: any) => {
      if (sorter && sorter.field) {
        // Convert antd sorter.order to our expected sortOrder format
        let sortOrder: 'asc' | 'desc';

        if (sorter.order === 'ascend') {
          sortOrder = 'asc';
        } else if (sorter.order === 'descend') {
          sortOrder = 'desc';
        } else {
          // When clicking the third time (cancel sort), cycle back to ascending
          sortOrder = 'asc';
        }

        handleSortChange(sorter.field as string, sortOrder);
      } else {
        handleSortChange('createdAt', 'desc');
      }
    },
    [handleSortChange]
  );

  const columns = useMemo<ColumnConfig<Role>[]>(
    () => [
      {
        title: t('roles.name.label'),
        key: 'name',
        dataIndex: 'name',
        sorter: true,
      },
      {
        title: 'Actions',
        key: 'actions',
        align: 'right',
        render: (_: any, record: Role) => (
          <ActionButtons
            showEdit={rbac.hasAccess(userPermissions, 'role.update')}
            showDelete={rbac.hasAccess(userPermissions, 'role.delete')}
            showView={rbac.hasAccess(userPermissions, 'role.view')}
            record={record}
            baseUrl="/dashboard/role"
            onDelete={handleDeleteRole}
            isDeleting={deleteRoleMutation.isPending && deleteRoleMutation.variables === record.id}
          />
        ),
      },
    ],
    [handleDeleteRole, deleteRoleMutation.isPending, deleteRoleMutation.variables]
  );

  return (
    <>
      <HeaderRow
        title={t('crud.list.title', { entity: t('roles.title') })}
        createRoute="create"
        showCreateButton={rbac.hasAccess(userPermissions, 'role.create')}
      />
      <div className="row mt-3">
        <RoleTable
          data={tableData}
          columns={columns}
          loading={
            isLoading || deleteRoleMutation.isPending || isFetching || isAvailableFiltersLoading
          }
          pagination={data}
          rowKey="id"
          showSizeChanger={true}
          onPageChange={handlePageChange}
          onPageSizeChange={handlePageSizeChange}
          showSearchBar={true}
          onSearchChange={handleSearchChange}
          showFilters={true}
          availableFilters={availableFilters?.data}
          onFilterChange={handleFilterChange}
          tableProps={{
            onChange: handleTableChange,
            sortDirections: ['ascend', 'descend'],
          }}
          sortField={queryParams.sortField}
          sortOrder={queryParams.sortOrder}
        />
      </div>
    </>
  );
};

export default WithPermissionCheck(RoleTableContainer, 'role.view');
